import { ReactNode, useEffect, useState } from "react";

import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button, FormLabel, Modal } from "@impero-com/ui";

import { UserOverview } from "api/generated/model/userOverview";
import {
    UserOrUserGroupTypeahead,
    UserTypeahead,
} from "components/userTypeahead";
import {
    UserOrUserGroupOverview,
    userOrUserGroupToPartialUpdateTarget,
} from "utils/user";

import * as theme from "./ReplaceUserModal.css";
interface ReplaceUserModalProps {
    open: boolean;
    replaceType: "WithUser" | "WithUserOrGroup";
    loading: boolean;
    onClose: () => void;
    onClick: (userOrGroup: UserOrUserGroupOverview) => void;
    user: UserOverview;
    error?: ReactNode;
}
export function ReplaceUserModal({
    open,
    replaceType,
    loading,
    onClose,
    onClick,
    user,
    error,
}: ReplaceUserModalProps) {
    const { t } = useTranslation();
    const [selectedUserOrGroup, setSelectedUserOrGroup] = useState<
        UserOrUserGroupOverview | undefined
    >(undefined);

    const filterUserOut = (userOrGroup: UserOrUserGroupOverview) => {
        // Filter out only if id and kind (User or UserGroup) matches the current user.
        const currentId = userOrUserGroupToPartialUpdateTarget(user);
        const anotherId = userOrUserGroupToPartialUpdateTarget(userOrGroup);
        return !(
            currentId.data === anotherId.data &&
            currentId.kind === anotherId.kind
        );
    };

    useEffect(() => {
        if (open) {
            /* eslint-disable react-hooks/set-state-in-effect */
            setSelectedUserOrGroup(undefined);
        }
    }, [open]);

    return (
        <Modal
            variant="content"
            open={open}
            onClose={onClose}
            title={t("AccessAndAssignment_ReplaceDialog_Title")}
            footer={
                <>
                    <Button onClick={onClose}>{t("Cancel")}</Button>
                    <Button
                        variant="primary"
                        disabled={selectedUserOrGroup === undefined}
                        loading={loading}
                        onClick={() => onClick(selectedUserOrGroup!)}
                    >
                        {t("AccessAndAssignment_ReplaceDialog_ReplaceButton")}
                    </Button>
                </>
            }
        >
            <FormLabel className={theme.replaceDialogText}>
                {t("AccessAndAssignment_ReplaceDialog_Text", {
                    user: formatUserName(user),
                })}
            </FormLabel>

            {replaceType === "WithUser" && (
                <UserTypeahead
                    id="replace-user-typeahead"
                    filter={filterUserOut}
                    value={selectedUserOrGroup as UserOverview}
                    onSelect={setSelectedUserOrGroup}
                />
            )}
            {replaceType === "WithUserOrGroup" && (
                <UserOrUserGroupTypeahead
                    id="replace-user-or-group-typeahead"
                    value={selectedUserOrGroup}
                    filter={filterUserOut}
                    onSelect={setSelectedUserOrGroup}
                />
            )}
            {error}
        </Modal>
    );
}
